import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import routes from '../../../setup/consts/routes';
import ScSectionHeader from '../ScSectionHeader';
import { IconBox } from '../../../assets/svg/icons';
import {
  deliveryRate as computeDeliveryRate,
  rateSeverity,
  RateSeverity,
} from '../../../pages/dashboard/fulfillment';
import {
  StyledFPAlert,
  StyledFPAlertAction,
  StyledFPAlertBody,
  StyledFPAlertIconTile,
  StyledFPAlertText,
  StyledFPBar,
  StyledFPBarMeta,
  StyledFPBarSegment,
  StyledFPBarWrapper,
  StyledFPCard,
  StyledFPEyebrow,
  StyledFPRateBar,
  StyledFPRate,
  StyledFPRateBlock,
  StyledFPSegmentCard,
  StyledFPSegmentCount,
  StyledFPSegmentLabel,
  StyledFPSegmentPct,
  StyledFPSegments,
  StyledFPSegmentTop,
  StyledFPSeeAllLink,
} from './styles';

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

interface FulfillmentPerformanceProps {
  delivered: number;
  inProgress: number;
  failed: number;
  returned: number;
  isLoading?: boolean;
}

const FulfillmentPerformance: React.FC<FulfillmentPerformanceProps> = ({
  delivered,
  inProgress,
  failed,
  returned,
  isLoading,
}) => {
  const history = useHistory();
  const intl = useIntl();

  const segmentList = [
    { key: 'delivered', label: intl.formatMessage({ id: 'dashboard.fulfillment.delivered', defaultMessage: 'Delivered' }), value: delivered, color: 'var(--brand-green)', bg: 'rgba(118,177,89,.06)', borderColor: 'var(--brand-green)' },
    { key: 'progress', label: intl.formatMessage({ id: 'dashboard.fulfillment.inProgress', defaultMessage: 'In progress' }), value: inProgress, color: 'var(--brand-ink)', bg: 'rgba(9,15,56,.04)', borderColor: 'var(--brand-ink)' },
    { key: 'failed', label: intl.formatMessage({ id: 'dashboard.fulfillment.failed', defaultMessage: 'Failed' }), value: failed, color: 'var(--danger)', bg: 'rgba(212,121,116,.06)', borderColor: 'var(--danger)' },
    { key: 'returned', label: intl.formatMessage({ id: 'dashboard.fulfillment.returned', defaultMessage: 'Returned' }), value: returned, color: 'var(--warning)', bg: 'rgba(218,146,19,.06)', borderColor: 'var(--warning)' },
  ];

  const total = delivered + inProgress + failed + returned;
  const rate = computeDeliveryRate({ delivered, inProgress, failed, returned });

  const SEVERITY_COLOR: Record<RateSeverity, string> = {
    good: 'var(--brand-green)',
    warn: 'var(--warning)',
    bad: 'var(--danger)',
  };
  const rateColor =
    rate === null ? 'var(--muted)' : SEVERITY_COLOR[rateSeverity(rate)];

  return (
    <StyledFPCard>
      <ScSectionHeader
        title={intl.formatMessage({ id: 'dashboard.fulfillment.title', defaultMessage: 'Fulfillment performance' })}
        subtitle={intl.formatMessage({ id: 'dashboard.fulfillment.subtitle', defaultMessage: 'How your active pipeline is moving' })}
        icon={<IconBox />}
        actions={
          <StyledFPSeeAllLink type="button" onClick={() => history.push(routes.ORDERS)}>
            {intl.formatMessage({ id: 'dashboard.fulfillment.seeAllOrders', defaultMessage: 'See all orders →' })}
          </StyledFPSeeAllLink>
        }
      />

      {/* Rate + stacked bar — side by side */}
      <StyledFPRateBar>
        <StyledFPRateBlock>
          <StyledFPRate $color={rateColor}>
            {isLoading ? '…' : rate === null ? '—' : `${rate}`}
            {!isLoading && rate !== null && <span>%</span>}
          </StyledFPRate>
          <StyledFPEyebrow>{intl.formatMessage({ id: 'dashboard.fulfillment.deliveryRate', defaultMessage: 'Delivery rate' })}</StyledFPEyebrow>
        </StyledFPRateBlock>

        <StyledFPBarWrapper>
          <StyledFPBar>
            {total > 0 && segmentList.map((seg) => (
              seg.value > 0 && (
                <StyledFPBarSegment
                  key={seg.key}
                  color={seg.color}
                  width={`${(seg.value / total) * 100}%`}
                  title={`${seg.label}: ${seg.value.toLocaleString()}`}
                />
              )
            ))}
          </StyledFPBar>
          <StyledFPBarMeta>
            <span>
              <strong>{isLoading ? '…' : total.toLocaleString()}</strong>{' '}{intl.formatMessage({ id: 'dashboard.fulfillment.acrossStages', defaultMessage: 'orders across these stages' })}
            </span>
            <span>{intl.formatMessage({ id: 'dashboard.fulfillment.updatedJustNow', defaultMessage: 'Updated just now' })}</span>
          </StyledFPBarMeta>
        </StyledFPBarWrapper>
      </StyledFPRateBar>

      <StyledFPSegments>
        {segmentList.map((seg) => {
          const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
          return (
            <StyledFPSegmentCard key={seg.key} bg={seg.bg} borderColor={seg.borderColor}>
              <StyledFPSegmentTop>
                <StyledFPSegmentCount>
                  {isLoading ? '…' : seg.value.toLocaleString()}
                </StyledFPSegmentCount>
                {!isLoading && <StyledFPSegmentPct>{pct}%</StyledFPSegmentPct>}
              </StyledFPSegmentTop>
              <StyledFPSegmentLabel>{seg.label}</StyledFPSegmentLabel>
            </StyledFPSegmentCard>
          );
        })}
      </StyledFPSegments>

      {!isLoading && failed > 0 && (
        <StyledFPAlert>
          <StyledFPAlertIconTile>
            <WarningIcon />
          </StyledFPAlertIconTile>
          <StyledFPAlertBody>
            <StyledFPAlertText>
              <strong>{failed.toLocaleString()} {intl.formatMessage({ id: 'dashboard.fulfillment.failed', defaultMessage: 'failed' })}</strong>
              {' '}{intl.formatMessage({ id: 'dashboard.fulfillment.failedAlertBody', defaultMessage: '— review to recover revenue. Each one is money waiting on the table.' })}
            </StyledFPAlertText>
          </StyledFPAlertBody>
          <StyledFPAlertAction
            type="button"
            onClick={() => history.push(routes.FAILED_ORDERS)}
          >
            {intl.formatMessage({ id: 'dashboard.fulfillment.reviewFailed', defaultMessage: 'Review failed →' })}
          </StyledFPAlertAction>
        </StyledFPAlert>
      )}
    </StyledFPCard>
  );
};

export default FulfillmentPerformance;
